import { SCENARIO_COLORS } from './analysisMetrics';

const NEUTRAL_PCT_THRESHOLD = 0.5;

/** Somme des impacts cashflow par actif (alignée sur le débogueur Analyse). */
export function getProjectionCashflowImpactTotal(row) {
  if (!row) return 0;
  if (row.cashflowImpactTotal != null) return row.cashflowImpactTotal;
  return (row.cashflowImpactPersonnel ?? 0) + (row.cashflowImpactCorporation ?? 0);
}

export function mergeProjectionsByYear(rowsA, rowsB) {
  const len = Math.max(rowsA?.length || 0, rowsB?.length || 0);
  const out = [];
  for (let i = 0; i < len; i += 1) {
    const a = rowsA[i] || {};
    const b = rowsB[i] || {};
    const year = a.year || b.year;
    out.push({
      year,
      yearIndex: i,
      netWorthA: a.netWorth ?? 0,
      netWorthB: b.netWorth ?? 0,
      totalAssetsA: a.totalAssets ?? 0,
      totalAssetsB: b.totalAssets ?? 0,
      immoA: a.immobilierTotal ?? 0,
      immoB: b.immobilierTotal ?? 0,
      placementsA: (a.placementsTotal != null ? a.placementsTotal : Math.max(0, (a.totalAssets || 0) - (a.immobilierTotal || 0) - (a.assuranceParticipativeTotal || 0))),
      placementsB: (b.placementsTotal != null ? b.placementsTotal : Math.max(0, (b.totalAssets || 0) - (b.immobilierTotal || 0) - (b.assuranceParticipativeTotal || 0))),
      assuranceA: a.assuranceParticipativeTotal ?? 0,
      assuranceB: b.assuranceParticipativeTotal ?? 0,
      cashflowA: getProjectionCashflowImpactTotal(a),
      cashflowB: getProjectionCashflowImpactTotal(b),
      cashflowEntityA: a.annualCashflowAfterTax ?? 0,
      cashflowEntityB: b.annualCashflowAfterTax ?? 0,
      revenueA: a.annualRevenueGross ?? 0,
      revenueB: b.annualRevenueGross ?? 0,
      taxA: a.annualTax ?? 0,
      taxB: b.annualTax ?? 0,
      gapNetWorth: (b.netWorth ?? 0) - (a.netWorth ?? 0),
      rowA: a,
      rowB: b,
    });
  }
  return out;
}

export function computeDelta(valueA, valueB, higherIsBetter = true) {
  const delta = (valueB ?? 0) - (valueA ?? 0);
  const absA = Math.abs(valueA ?? 0);
  const deltaPct = absA > 0 ? (delta / absA) * 100 : (delta !== 0 ? null : 0);

  let tone = 'neutral';
  if (higherIsBetter === null) {
    tone = Math.abs(deltaPct ?? 0) < NEUTRAL_PCT_THRESHOLD ? 'neutral' : 'info';
  } else if (Math.abs(deltaPct ?? 0) < NEUTRAL_PCT_THRESHOLD && delta === 0) {
    tone = 'neutral';
  } else if (higherIsBetter) {
    if (delta > 0) tone = 'positive';
    else if (delta < 0) tone = 'negative';
  } else {
    if (delta < 0) tone = 'positive';
    else if (delta > 0) tone = 'negative';
  }

  return { delta, deltaPct, tone };
}

export function getDeltaToneClasses(tone) {
  switch (tone) {
    case 'positive':
      return {
        text: 'text-[var(--color-success)]',
        bg: 'bg-[var(--color-success)]/10',
        border: 'border-[var(--color-success)]/25',
      };
    case 'negative':
      return {
        text: 'text-[var(--color-destructive)]',
        bg: 'bg-[var(--color-destructive)]/10',
        border: 'border-[var(--color-destructive)]/25',
      };
    case 'info':
      return {
        text: 'text-primary',
        bg: 'bg-primary/10',
        border: 'border-primary/25',
      };
    default:
      return {
        text: 'text-muted-foreground',
        bg: 'bg-muted/40',
        border: 'border-border',
      };
  }
}

export function formatDelta(delta, deltaPct, format, formatCurrency) {
  const sign = delta > 0 ? '+' : '';
  if (format === 'percent') {
    const pctStr = deltaPct != null ? ` (${sign}${deltaPct.toFixed(1)}%)` : '';
    return `${sign}${delta.toFixed(1)}%${pctStr}`;
  }
  if (format === 'currency') {
    const pctStr = deltaPct != null ? ` (${sign}${deltaPct.toFixed(1)}%)` : '';
    return `${sign}${formatCurrency(delta).replace(/^-/, '−')}${pctStr}`;
  }
  return `${sign}${delta}`;
}

export function formatYAxis(value) {
  if (Math.abs(value) >= 1e6) return `${(value / 1e6).toFixed(1)}M`;
  if (Math.abs(value) >= 1e3) return `${(value / 1e3).toFixed(0)}k`;
  return value;
}

export function getScenarioLabel(scenarioId, scenarios, activeScenarioId) {
  if (scenarioId === '__current__') {
    return activeScenarioId ? 'Scénario actif' : 'Données courantes';
  }
  return scenarios.find((s) => s.id === scenarioId)?.name || 'Scénario';
}

export { SCENARIO_COLORS, NEUTRAL_PCT_THRESHOLD };
